import {createContext, use} from 'react'

/** The page whose implicit context should be resolved. */
export interface ImplicitContextPage {
  /** Repository owner, or `''` when the page is not a repository page. */
  owner: string
  /** Repository name, or `''` when the page is not a repository page. */
  repo: string
  /** The dotcom-relative URL of the page the user is viewing. */
  url: string
  /** The page's document title, used as the `github_url` fallback chip label. */
  title?: string
}

/**
 * The host page whose entities seed the composer's implicit context (the repo,
 * file, issue, PR, etc. the user is currently viewing). Repository pages carry
 * `owner`/`repo`; non-repository pages carry empty `owner`/`repo` with just the
 * `url`/`title` so the resolver can synthesize a `github_url` fallback. `null`
 * only when there is no page context to attach at all — e.g. the standalone chat
 * route, or the GitHub home/dashboard — which disables implicit-context
 * resolution.
 *
 * Kept as its own single-value context so consumers only re-render when the
 * current page actually changes.
 */
export const ImplicitContextPageContext = createContext<ImplicitContextPage | null>(null)

/**
 * Resolves the freshest host page at submit time. Kept separate from
 * {@link ImplicitContextPageContext} (which holds the reactive value for chip
 * display) so a submission resolves against the current location even when the
 * reactive value is momentarily stale — e.g. during the line-selection debounce
 * window or after a router-driven same-route hash navigation that fires no DOM
 * event. `null` when no resolver is provided; consumers then fall back to the
 * reactive context value.
 */
export const ImplicitContextPageResolverContext = createContext<(() => ImplicitContextPage | null) | null>(null)

/** Reads the current host page for implicit context, or `null` when unknown. */
export function useImplicitContextPage(): ImplicitContextPage | null {
  return use(ImplicitContextPageContext)
}
